import { KeyValue } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { StringValidators } from '../../validators/string.validator';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { AppStore, SnackbarWarningAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { KeyValueExtended } from '../../models/key-value-extended';
import { APP_CONFIG_MAX_ITEMS_SELECT } from '../../models/alden-constants';
import { AldenService } from '../../services/alden.service';

@Component({
  selector: 'smt-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  host: { class: 'smt-search-select' }
})
export class SearchSelectComponent implements OnInit, OnChanges {
  @Output() optionSelected = new EventEmitter<MatAutocompleteSelectedEvent>();
  @Output() optionChanged = new EventEmitter<void>();
  @Output() removedValue = new EventEmitter<void>();

  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() excludeControlName: string;
  @Input() label: string;
  @Input() options: KeyValueExtended<any, string>[];
  @Input() disabled: boolean;
  @Input() multiple: boolean;
  @Input() canClean: boolean;
  @Input() showTooltip: boolean;
  @Input() searchQuery!: (value: string) => Promise<any>;
  @Input() searchValidator!: (value: string) => boolean;
  @Input() useChips: boolean;
  @Input() notAddStringValidator: boolean
  @Input() optionCSS: string;
  @Input() maxItems: number;

  // Al poner el ngIf es necesario tener la variable
  @ViewChild('selectInput') selectInput!: MatInput | undefined;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  filteredOptions: Observable<KeyValue<string, string>[]>;
  selectedOptions: KeyValue<string, string>[];
  excludedOptions: KeyValue<string,string>[];

  control: AbstractControl<any, any>;
  excludedControl: AbstractControl<any, any>;
  search: boolean;
  searching: boolean;
  shouldFocus: boolean;

  searchErrorMessage: string;

  showMaxItemsMsg: boolean = false;

  constructor(private cdRef: ChangeDetectorRef, protected store: Store<AppStore>, private aldenService: AldenService) {
    this.canClean = true;
    this.showTooltip = true;
    this.maxItems = this.aldenService.getAppConfigProp(APP_CONFIG_MAX_ITEMS_SELECT);
  }

  ngOnInit(): void {
    this.control = this.group.get(this.controlName);
    if(!this.notAddStringValidator) {
      this.control.addValidators(StringValidators.NotStringValidator);
      this.control.updateValueAndValidity();
    }
    
    if(this.excludeControlName){
      this.excludedControl = this.group.get(this.excludeControlName);
    
      this.excludedControl.addValidators(StringValidators.NotStringValidator);
      this.excludedControl.updateValueAndValidity();
    }
    
    if(this.searchQuery) {
      this.search = true;
      this.control?.valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged()
      ).subscribe(value => {       
        if(value) {
          if(typeof value === 'string')
            this.searchValue(value);
        } else {
          this.searchErrorMessage = null;
        }
      });
    }

    if(this.disabled === undefined) {
      this.disabled = this.group?.disabled || this.control?.disabled ? true : this.disabled;
    }

    this.useChips ??= false;
 
    if(this.multiple) {
      this.selectedOptions = this.control.value;
      if(this.useChips)
        this.excludedOptions = this.excludedControl.value;
    }

    let firstValue = true;
    this.filteredOptions = this.control?.valueChanges.pipe(
      map((option: KeyValue<string, string>[] | KeyValue<string, string> | string) => {
        let value = option;
        if(typeof option !== 'string') {
          if(Array.isArray(option)) {
            this.selectedOptions = value as KeyValue<string, string>[];
          } else {
            value = option?.value;
          }
        }

        if(typeof option === 'string' && !firstValue) {
          if (this.multiple)
            this.control.setValue(this.selectedOptions);
          if (this.useChips)
            this.excludedControl.setValue(this.excludedOptions);
          this.optionChanged.emit();
        }
        
        firstValue = false;
        const filtered = value && typeof option === 'string'
          ? this._filter(value as string)
          : (this.options?.slice() ?? []);

        this.showMaxItemsMsg = filtered?.length > this.maxItems;
        return this.maxItems && this.showMaxItemsMsg ? filtered?.slice(0, this.maxItems) : filtered;
      }),
      tap(_filtered => {
        this.cdRef.detectChanges();
      })
    );

    // Si es un campo requerido y solo tiene un valor disponible seleccionarlo
    if(this.isRequired(this.controlName, this.group)) {
      if (this.options?.length === 1 && !this.control?.value) {
        const option = this.options[0];
        this.control?.setValue(option);
        this.optionSelected.emit();
      }
    }
    this.disable(this.disabled);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.control = this.group.get(this.controlName);

    const disabledChange = changes["disabled"];
    if(disabledChange !== undefined) {
      this.disable(disabledChange.currentValue);
    }

    const optionsChange = changes["options"];
    if(optionsChange !== undefined) {
      this.options = optionsChange.currentValue;
      this.checkSelectedOptions();
      this.checkExcludedOptions();
      this.group.get(this.controlName).updateValueAndValidity();
      if(this.excludeControlName) this.group.get(this.excludeControlName).updateValueAndValidity();
    }

    const notAddStringValidatorChange = changes["notAddStringValidator"];
    if(notAddStringValidatorChange && !notAddStringValidatorChange.firstChange) {
      const notAddStringValidator = notAddStringValidatorChange.currentValue;
      if (!notAddStringValidator) {
        this.control.addValidators(StringValidators.NotStringValidator);
      } else {
        this.control.removeValidators(StringValidators.NotStringValidator);
      }

      this.control.updateValueAndValidity();
    }

    this.cdRef.detectChanges();
  }

  checkSelectedOptions() {
    if(this.selectedOptions) {
      this.selectedOptions = this.selectedOptions.filter((selectedOpt) => this.options.find((opt) => opt.key == selectedOpt.key));
      const control = this.group?.get(this.controlName);
      control.setValue(this.selectedOptions);
    }
  }

  checkExcludedOptions() {
    if(this.excludedOptions) {
      this.excludedOptions = this.excludedOptions.filter((selectedOpt) => this.options.find((opt) => opt.key == selectedOpt.key));
      const control = this.group.get(this.excludeControlName);
      control.setValue(this.excludedOptions);
    }
  }

  disable(disable: boolean) {
    const control = this.group?.controls[this.controlName];
    disable ? control?.disable() : control?.enable();
  }

  private _filter(value: string): KeyValue<string, string>[] {
    const filterValue = this.normalizeText(value);
    return this.options?.filter(option => this.normalizeText(option.value!).toLowerCase().includes(filterValue));
  }

  displayFn(option: KeyValue<string, string>): string {
    return option && option.value ? option.value : '';
  }
  
  isSelected(option: KeyValue<string, string>): boolean {
    const value = this.group.get(this.controlName).value;
    return value && typeof option !== 'string' ? value === option : false;
  }

  isSelectedMult(option: KeyValue<string, string>) : boolean {
    return this.selectedOptions ? this.selectedOptions.some((opt) => opt.key === option.key) : false;
  }

  selectedOption(event: MatAutocompleteSelectedEvent) {
    if(this.multiple) {
      const option = event.option.value;

      if(this.selectedOptions?.length > 0) {
        if(this.isSelectedMult(option)) {
          this.remove(option);
          return;
        }
        this.selectedOptions.push(option);
      } else {
          this.selectedOptions = [option];
      }
      
      const control = this.group.get(this.controlName);
      control.setValue(this.selectedOptions);

      this.selectInput.value = '';
      this.optionSelected.emit();
    } else {
      this.optionSelected.emit(event);
    }
  }

  remove(option: KeyValue<string, string>): void {
    let index = this.selectedOptions.indexOf(option);
    if(this.useChips && index < 0){
      index = this.excludedOptions.indexOf(option);

      this.excludedOptions.splice(index, 1);

      const control = this.group.get(this.excludeControlName);
      control.setValue(this.excludedOptions);
    }else{
      this.selectedOptions.splice(index, 1);
          
      const control = this.group.get(this.controlName);
      control.setValue(this.selectedOptions);
    }

    this.removedValue.emit();
  }

  cleanValue() {
    const control = this.group.controls[this.controlName];
    control.reset();

    if(this.multiple) {
      this.selectedOptions = [];
    }
    if(this.useChips){
      const control = this.group.controls[this.excludeControlName];
      control.reset();
      this.excludedOptions = [];
    }

    this.removedValue.emit();
  }

  isRequired(controlName: string, formGroup: FormGroup<any>): boolean {
    return formGroup.controls[controlName].hasValidator(Validators.required);
  }

  isInvalid(controlName: string, formGroup: FormGroup<any>): boolean {
    const control = formGroup.controls[controlName];
    return control.errors !== null && control.touched;
  }

  async searchValue(value) {
    const searchValidator = this.searchValidator ?? StringValidators.isSearcheable;
    if (searchValidator(value)) {
      this.searching = true;
      this.autocompleteTrigger.closePanel();

      // Quitamos el error de búsqueda
      this.searchErrorMessage = null;

      try {
        this.options = await this.searchQuery(value);
        this.filteredOptions = of(this.options);
        if (this.options?.length === 1) {
          const option = this.options[0];
          this.control?.setValue(option);
          this.optionSelected.emit();
        } else {
          this.autocompleteTrigger.openPanel();
        }
      } catch (err) {
        console.error(err);
        this.store.dispatch(new SnackbarWarningAction('No se pudo obtener respuesta.'));
      } finally {
        this.searching = false;
      }
    } else {
      // Añadimos el error de búsqueda
      if(value)
        this.searchErrorMessage = 'ALDEN.MESSAGES.ERRORS.INVALID_SEARCH';
      this.autocompleteTrigger.closePanel();
      this.options = [];
    }
  }

  focus() {
    // El elemento puede aún no existir, asique debemos esperar
    setTimeout(() => {
      // Forzar la detección
      const value = this.control.value;
      this.control.setValue(value);
      this.cdRef.detectChanges();

      // Abrir el panel
      this.autocompleteTrigger?.openPanel();
      this.selectInput?.focus();
    }, 100);
  }

  exclude(option: KeyValue<string, string>){
    if(this.excludedOptions?.length > 0) {
      this.excludedOptions.push(option);
    } else {
      this.excludedOptions = [option];
    }
    
    const control = this.group.get(this.excludeControlName);
    control.setValue(this.excludedOptions);

    this.selectInput.value = '';
    this.optionSelected.emit();
    this.autocompleteTrigger.closePanel();
  }

  private normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
